import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE } from "../utils/api";

/* ─────────────────────────────────────────────────────────────────────
   NEW-FIRM registration (full KYC). Documents upload to the backend; the
   details + document URLs are submitted to a SEPARATE Google Form/Sheet
   (registration tag — distinct from the profile KYC).
   👉 Create a Google Form with the questions below (in this order) and
   paste its `formResponse` URL + each `entry.XXXX` id here.
──────────────────────────────────────────────────────────────────────*/
const REG_KYC_FORM = {
  // Reuses the existing registration Google Form (1FAIpQLScPj8...). The 9
  // detail fields are already wired; ADD 4 new "…Document URL" questions to
  // that form and paste their entry ids below (replace the PASTE_ ones).
  url: "https://docs.google.com/forms/d/e/1FAIpQLScPj8FGWqQPdJkLuSXMLFHnxTm9JT_LI48ioHs0mpAeZxovEg/formResponse",
  entries: {
    partyName: "entry.555086582",
    gstin: "entry.678877658",
    district: "entry.877677000",
    taluka: "entry.1597346478",
    area: "entry.1768756891",
    contactPerson: "entry.1764051155",
    contactNo: "entry.710362458",
    email: "entry.1564598098",
    type: "entry.1895555529",
    aadhaarUrl: "entry.1377765587",
    panUrl: "entry.89718608",
    chequeUrl: "entry.428183029",
    godownBillUrl: "entry.931306378",
  },
};

const TYPE_OPTIONS = [
  "Dealer",
  "Builder",
  "Contractor",
  "Fabricator",
  "Industrial",
  "End User",
  "Project",
];

const DOCS = [
  { key: "aadhaar", label: "Aadhaar Card" },
  { key: "pan", label: "PAN Card" },
  { key: "cheque", label: "Cancelled Cheque" },
  { key: "godownBill", label: "Godown Electricity Bill" },
];

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export default function RegistrationKyc() {
  const navigate = useNavigate();
  const location = useLocation();

  const [form, setForm] = useState({
    partyName: "",
    gstin: (location.state?.gst || "").toUpperCase(),
    district: "",
    taluka: "",
    area: "",
    contactPerson: "",
    contactNo: location.state?.contactNo || "",
    email: "",
    type: "",
  });
  const [docUrls, setDocUrls] = useState({});
  const [uploading, setUploading] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleUpload = async (key, file) => {
    if (!file) return;
    if (!form.gstin && !form.contactNo) {
      toast.error("Pehle GST / Contact No bharein, phir document upload karein.");
      return;
    }
    setUploading((p) => ({ ...p, [key]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("type", key);
      fd.append("gst", form.gstin);
      fd.append("phone", form.contactNo);
      const res = await fetch(`${API_BASE}/api/registration-kyc/upload`, {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        setDocUrls((p) => ({ ...p, [key]: data.url }));
        toast.success(`${key} uploaded ✓`);
      } else {
        toast.error(data.message || "Upload failed.");
      }
    } catch {
      toast.error("Upload failed. Please try again.");
    } finally {
      setUploading((p) => ({ ...p, [key]: false }));
    }
  };

  const submitToSheet = async () => {
    const fd = new FormData();
    Object.entries(REG_KYC_FORM.entries).forEach(([k, entry]) => {
      let val = "";
      if (k === "aadhaarUrl") val = docUrls.aadhaar;
      else if (k === "panUrl") val = docUrls.pan;
      else if (k === "chequeUrl") val = docUrls.cheque;
      else if (k === "godownBillUrl") val = docUrls.godownBill;
      else val = form[k];
      fd.append(entry, val || "");
    });
    await fetch(REG_KYC_FORM.url, { method: "POST", body: fd, mode: "no-cors" });
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;

    const req = [
      ["partyName", "Party Name"],
      ["district", "District"],
      ["taluka", "Taluka"],
      ["area", "Area"],
      ["contactPerson", "Contact Person Name"],
    ];
    for (const [k, label] of req) {
      const v = String(form[k] || "").trim();
      if (!v) return toast.error(`${label} is required.`);
      if (/[0-9]/.test(v)) return toast.error(`${label} should not contain numbers.`);
    }
    if (!GSTIN_RE.test(form.gstin.trim().toUpperCase()))
      return toast.error("Enter a valid 15-digit GSTIN.");
    if (!/^[6-9]\d{9}$/.test(form.contactNo))
      return toast.error("Contact No must be a valid 10-digit mobile number.");
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim()))
      return toast.error("Enter a valid Email Id.");
    if (!form.type) return toast.error("Please select a Type.");

    for (const d of DOCS) {
      if (!docUrls[d.key]) return toast.error(`Please upload ${d.label}.`);
    }

    setBusy(true);
    try {
      if (Object.values(REG_KYC_FORM.entries).some((e) => e.includes("PASTE_"))) {
        toast.error("Registration form not fully connected yet. Please contact admin.");
        setBusy(false);
        return;
      }
      await submitToSheet();
      toast.success("✅ Registration submitted for approval!");
      setTimeout(() => navigate("/"), 1500);
    } catch {
      toast.error("Submit failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const field = (label, k, opts = {}) => (
    <div>
      <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
        {label} {opts.required && <span className="text-red-500">*</span>}
      </label>
      <input
        value={form[k]}
        onChange={(e) => {
          let v = e.target.value;
          if (opts.onlyDigits) v = v.replace(/\D/g, "");
          else if (opts.noDigits) v = v.replace(/[0-9]/g, "");
          else if (opts.upper) v = v.toUpperCase().replace(/\s/g, "");
          set(k, v);
        }}
        placeholder={opts.placeholder || ""}
        inputMode={opts.inputMode}
        maxLength={opts.maxLength}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800"
      />
    </div>
  );

  return (
    <>
      <div
        className="fixed inset-0 bg-cover bg-center blur-sm z-[-1]"
        style={{ backgroundImage: "url('/images/bg_friends.jpeg')" }}
      />
      <div className="fixed inset-0 bg-white/25 backdrop-blur-sm z-[-1]" />

      <div className="min-h-screen flex items-center justify-center py-6 px-3">
        <form
          onSubmit={handleSubmit}
          className="bg-white w-full max-w-4xl p-5 sm:p-8 rounded-2xl shadow-xl"
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <img src="/images/Logo_Thumbnail-removebg.png" alt="Logo" className="h-14" />
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">KYC</h2>
          </div>

          {/* Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3">
            {field("Party Name", "partyName", { required: true, noDigits: true })}
            {field("GSTIN/UIN", "gstin", {
              required: true,
              upper: true,
              maxLength: 15,
              placeholder: "24ABCDE1234F1Z5",
            })}
            {field("District", "district", { required: true, noDigits: true })}
            {field("Taluka", "taluka", { required: true, noDigits: true })}
            {field("Area", "area", { required: true, noDigits: true })}
            {field("Contact Person Name", "contactPerson", {
              required: true,
              noDigits: true,
            })}
            {field("Contact No", "contactNo", {
              required: true,
              onlyDigits: true,
              inputMode: "numeric",
              maxLength: 10,
            })}
            {field("Email Id", "email", { placeholder: "optional" })}
            <div>
              <label className="block text-xs font-bold text-gray-700 mb-1 text-left">
                Type <span className="text-red-500">*</span>
              </label>
              <select
                value={form.type}
                onChange={(e) => set("type", e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-gray-800"
              >
                <option value="">-- Select Type --</option>
                {TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Documents */}
          <h3 className="text-sm font-bold text-gray-800 mt-6 mb-2">
            Upload Documents <span className="text-red-500">*</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DOCS.map((d) => (
              <label
                key={d.key}
                className={`flex items-center justify-between gap-2 border rounded-lg px-3 py-2.5 text-sm cursor-pointer ${
                  docUrls[d.key]
                    ? "border-emerald-300 bg-emerald-50"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <span className="font-semibold text-gray-700">{d.label}</span>
                <span className="text-xs font-bold">
                  {uploading[d.key]
                    ? "Uploading…"
                    : docUrls[d.key]
                    ? "✓ Uploaded"
                    : "Choose file"}
                </span>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  className="hidden"
                  onChange={(e) => handleUpload(d.key, e.target.files?.[0])}
                />
              </label>
            ))}
          </div>

          <button
            type="submit"
            disabled={busy}
            className="w-full mt-6 bg-gray-800 text-white font-bold py-3 rounded-xl hover:bg-gray-900 disabled:opacity-50 transition"
          >
            {busy ? "Submitting…" : "Submit Registration"}
          </button>

          <p className="mt-4 text-sm text-center">
            Already registered?{" "}
            <Link to="/" className="text-blue-600 hover:underline font-semibold">
              Login
            </Link>
          </p>
        </form>
      </div>

      <ToastContainer position="top-center" autoClose={4000} hideProgressBar theme="colored" />
    </>
  );
}
